import * as React from "react";
import { useRef, useEffect, useContext, useCallback, useMemo } from "react";
import styled, { css } from "styled-components";
import CopyToClipboardCapture from "components/common/CopyToClipboardCapture";
import RenderCompletionCallback from "views/components/widgets/RenderCompletionCallback";
import InvalidLicenseWidgetInfo from "search/InvalidLicenseWidgetInfo";
import { INFINITE_SCROLL_DIRECTION_UP } from "logview/Constants";
import BulkActionsRow from "components/common/EntityDataTable/BulkActionsRow";
import LogViewSelectedEntitiesProvider from "logview/components/contexts/LogViewSelectedEntitiesProvider";
import SelectableLogViewMessagesProvider from "logview/components/contexts/SelectableLogViewMessagesProvider";
import LogViewSkeleton from "data-lake/preview/LogViewSkeleton";
import InteractiveContext from "views/components/contexts/InteractiveContext";
import ExportSettingsTimerange from "./ExportSettingsTimerange";
import MessageDetailsProvider from "./contexts/MessageDetailsProvider";
import LogViewMessageDetails from "./LogViewMessageDetails";
import LogViewTable from "./LogViewTable";
import LogViewStateProvider from "./contexts/LogViewStateProvider";
import LogViewLayoutContext from "./contexts/LogViewLayoutContext";
const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;
const TableContainer = styled(CopyToClipboardCapture)(
  ({ theme }) => css`
    font-size: ${theme.fonts.size.small};
    flex-grow: 1;
    overflow-x: auto;
    position: relative;
  `
);
const StyledBulkActionsRow = styled(BulkActionsRow)(
  ({ theme }) => css`
    padding-bottom: ${theme.spacings.sm};
  `
);
const _formatOnCopyToClipboard = (selection) => selection.toString().split(/\n\u200b\n|\u200c\n/).join(" ");
const LogViewWidget = ({
  children = void 0,
  effectiveTimerange = void 0,
  onLoadMessage,
  config,
  defaultFields,
  initialAfter,
  messages,
  setLoadingState,
  editing,
  fields,
  onChangeConfig,
  onLoadMessages,
  renderFieldName = void 0,
  renderFieldValue = void 0,
  infiniteScrollDirection = INFINITE_SCROLL_DIRECTION_UP,
  bulkSelection = {},
  bottomSection = void 0,
  loadedAllMessages,
  onOpenMessageDetails = void 0,
  isFetchingFirstPage = false,
  hasExecutedSearch = void 0,
  placeholder = void 0,
  timestampField = "timestamp",
  isFavoriteMessageFieldsEnabled = false
}) => {
  const columns = config.fields;
  const interactive = useContext(InteractiveContext);
  const tableRef = useRef(null);
  const tableHeaderRef = useRef(null);
  const pageRefs = useRef({});
  const renderComplete = useContext(RenderCompletionCallback);
  const _onChangeConfig = useCallback(
    (newConfig) => {
      setLoadingState(true);
      return onChangeConfig(newConfig).then(() => setLoadingState(false));
    },
    [onChangeConfig, setLoadingState]
  );
  useEffect(() => {
    renderComplete();
  }, [renderComplete]);
  const displayBulkAction = !!(bulkSelection == null ? void 0 : bulkSelection.actions) && !editing;
  const displayBulkSelectCol = (typeof (bulkSelection == null ? void 0 : bulkSelection.onChangeSelection) === "function" || displayBulkAction) && !editing && interactive;
  const _isEntitySelectable = useCallback(
    (entity) => {
      if (!displayBulkSelectCol || !entity.message._id) {
        return false;
      }
      if (typeof (bulkSelection == null ? void 0 : bulkSelection.isEntitySelectable) === "function") {
        return bulkSelection == null ? void 0 : bulkSelection.isEntitySelectable(entity);
      }
      return true;
    },
    [displayBulkSelectCol, bulkSelection]
  );
  const logViewLayout = useMemo(() => ({ timestampField }), [timestampField]);
  return /* @__PURE__ */ React.createElement(LogViewLayoutContext.Provider, { value: logViewLayout }, /* @__PURE__ */ React.createElement(InvalidLicenseWidgetInfo, { featureName: "log view" }), /* @__PURE__ */ React.createElement(
    LogViewStateProvider,
    {
      pageRefs,
      onLoadMessages,
      initialAfter,
      effectiveTimerange,
      infiniteScrollDirection,
      messages,
      loadedAllMessages,
      tableHeaderRef,
      tableRef
    },
    ({ resetListState }) => /* @__PURE__ */ React.createElement(MessageDetailsProvider, null, /* @__PURE__ */ React.createElement(
      SelectableLogViewMessagesProvider,
      {
        isEntitySelectable: _isEntitySelectable,
        displayBulkSelectCol
      },
      /* @__PURE__ */ React.createElement(LogViewSelectedEntitiesProvider, { bulkSelection }, /* @__PURE__ */ React.createElement(Container, null, /* @__PURE__ */ React.createElement(ExportSettingsTimerange, { sort: config.sort }), interactive && !editing && !!(bulkSelection == null ? void 0 : bulkSelection.actions) && /* @__PURE__ */ React.createElement(StyledBulkActionsRow, { bulkActions: bulkSelection.actions }), /* @__PURE__ */ React.createElement(TableContainer, { formatSelection: _formatOnCopyToClipboard }, children == null ? void 0 : children({ resetListState }), isFetchingFirstPage ? /* @__PURE__ */ React.createElement(LogViewSkeleton, { columns: config.fields }) : /* @__PURE__ */ React.createElement(
        LogViewTable,
        {
          placeholder,
          hasExecutedSearch,
          columns,
          fields,
          infiniteScrollDirection,
          tableRef,
          tableHeaderRef,
          pageRefs,
          bottomSection,
          isEntitySelectable: _isEntitySelectable,
          displayBulkSelectCol,
          onOpenMessageDetails
        }
      ), /* @__PURE__ */ React.createElement(
        LogViewMessageDetails,
        {
          defaultFields,
          editing,
          renderFieldName,
          renderFieldValue,
          onLoadMessage,
          onChangeConfig: _onChangeConfig,
          widgetConfig: config,
          isFavoriteMessageFieldsEnabled
        }
      ))))
    ))
  ));
};
export default LogViewWidget;
